import { FilterLinesIcon, IconSize } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Checkbox, Dot, Stack, Text } from '@prophecy/ui-v3';
import { Popover } from '@prophecy/ui-v3/Popover';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  EMPTY_PROJECT_LISTING_FILTERS,
  hasActiveProjectListingFilters,
  type ProjectListingFacets,
  type ProjectListingFilters,
  toggleProjectListingEntityKind,
  toggleProjectListingProvider
} from './entityFilterUtils';

type Props = {
  facets: ProjectListingFacets;
  filters: ProjectListingFilters;
  onChange: (filters: ProjectListingFilters) => void;
};

const Panel = styled(Stack)`
  background: ${theme.colors.white};
`;

const Header = styled(Stack)`
  border-bottom: 1px solid ${theme.colors.gray100};
`;

const Footer = styled(Stack)`
  border-top: 1px solid ${theme.colors.gray100};
`;

const ActiveDot = styled(Dot)`
  position: absolute;
  top: ${theme.spaces.x4};
  right: ${theme.spaces.x4};
  box-shadow: 0 0 0 2px ${theme.colors.white};
`;

const TriggerWrap = styled.span`
  position: relative;
  display: inline-flex;
`;

const SectionLabel = styled(Text)`
  padding: ${theme.spaces.x8} ${theme.spaces.x8} 0;
`;

export function ProjectListingFilter({ facets, filters, onChange }: Props) {
  const [open, setOpen] = useState(false);
  // Staged selection: edits live here until the user clicks "Apply Changes".
  const [draft, setDraft] = useState<ProjectListingFilters>(filters);

  // Keep the draft in sync with committed filters whenever the popover (re)opens
  // or the applied filters change underneath it (e.g. tab/workspace switch).
  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const hasActiveFilter = hasActiveProjectListingFilters(filters);
  const hasDraftSelection = hasActiveProjectListingFilters(draft);
  const { kindOptions, providerOptions } = facets;

  const handleApply = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleCancel = () => {
    setDraft(filters);
    setOpen(false);
  };

  const overlay = (
    <Panel role='dialog' aria-label='Project filters' direction='vertical' width='320px'>
      <Header
        direction='horizontal'
        align='space-between'
        alignY='center'
        padding={`${theme.spaces.x12} ${theme.spaces.x16}`}>
        <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900}>
          Filter projects with
        </Text>
        <Button
          variant='linkGray'
          size='s'
          disabled={!hasDraftSelection}
          onClick={() => setDraft(EMPTY_PROJECT_LISTING_FILTERS)}
          data-test-id='project-listing-filter-clear-all'>
          Clear all
        </Button>
      </Header>

      <Stack direction='vertical' gap={theme.spaces.x4} padding={theme.spaces.x8}>
        {kindOptions.map((option) => (
          <Stack key={option.id} direction='horizontal' align='space-between' alignY='center'>
            <Checkbox
              checked={draft.kinds.includes(option.id)}
              onChange={() => setDraft((current) => toggleProjectListingEntityKind(current, option.id))}
              data-test-id={`project-listing-filter-kind-${option.id}`}>
              {option.label}
            </Checkbox>
            <Text level='sm' tone={theme.colors.gray500}>
              {option.count}
            </Text>
          </Stack>
        ))}
      </Stack>

      <SectionLabel level='xs' weight={theme.fontWeight.semiBold} tone={theme.colors.gray500}>
        Provider
      </SectionLabel>
      <Stack direction='vertical' gap={theme.spaces.x4} padding={theme.spaces.x8}>
        {providerOptions.map((option) => (
          <Stack key={option.id} direction='horizontal' align='space-between' alignY='center'>
            <Checkbox
              checked={draft.providers.includes(option.id)}
              onChange={() => setDraft((current) => toggleProjectListingProvider(current, option.id))}
              data-test-id={`project-listing-filter-provider-${option.id}`}>
              {option.label}
            </Checkbox>
            <Text level='sm' tone={theme.colors.gray500}>
              {option.count}
            </Text>
          </Stack>
        ))}
      </Stack>

      <Footer
        direction='horizontal'
        align='end'
        alignY='center'
        gap={theme.spaces.x8}
        padding={`${theme.spaces.x12} ${theme.spaces.x16}`}>
        <Button variant='secondaryGray' size='s' onClick={handleCancel} data-test-id='project-listing-filter-cancel'>
          Cancel
        </Button>
        <Button variant='primary' size='s' onClick={handleApply} data-test-id='project-listing-filter-apply'>
          Apply Changes
        </Button>
      </Footer>
    </Panel>
  );

  return (
    <Popover
      visible={open}
      onVisibleChange={setOpen}
      overlay={overlay}
      placement='bottom'
      align='end'
      data-test-id='project-listing-filter'>
      <TriggerWrap>
        <Button
          aria-label='Filter projects'
          aria-expanded={open}
          data-test-id='project-listing-filter-button'
          icon={<FilterLinesIcon type='default' color={theme.colors.gray700} size={IconSize.m} />}
          variant='secondaryGray'
          size='xs'
        />
        {hasActiveFilter && <ActiveDot size='s' data-test-id='project-listing-filter-active-dot' />}
      </TriggerWrap>
    </Popover>
  );
}
